import React, { useEffect } from 'react';
import { Box, Button, Grid, Typography, CardContent, Card } from '@mui/material';
import { useTranslation } from 'react-i18next';
import favorite from "../../../svg/like.svg";
import share from "../../../svg/sharePrimary.svg";
import whatsapp from "../../../svg/whatsappPrimary.svg";
import Favfill from "../../../assets/img/favadd.svg";
import { styled } from '@mui/system';
import MagazinesCarousel from '../MobileMagazineCarousel';
import { useNavigate } from 'react-router-dom';
import { useDownloadApp } from '../../../Contexts/DownloadAppContext';
import { BookContentType } from '../../Epub';

interface BookDetailsCardProps {
    bookDetails?: any;
    magazinesDetails?: any[];
    totalRecords?: any;
    isLiked: boolean;
    kalyanDetails?: any;
    vivekVaniDetails?: any;
    geetGovindDetails?: any;
    selectedMagazineId?: any;
    magazineLang?: any;
    magazineLanguageId?: any;
    itemType?: string;
    onToggleLike: () => void;
}

const BookDetailsCard: React.FC<BookDetailsCardProps> = ({
    bookDetails,
    magazinesDetails,
    isLiked,
    onToggleLike,
    totalRecords,
    kalyanDetails,
    vivekVaniDetails,
    geetGovindDetails,
    selectedMagazineId,
    magazineLang,
    magazineLanguageId,
    itemType,
}: any) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { setShowDownloadPopup } = useDownloadApp();
    const getMonthName = (monthNumber: number): string => {
        const monthNames = [
            t("January_tr"),
            t("February_tr"),
            t("March_tr"),
            t("April_tr"),
            t("May_tr"),
            t("June_tr"),
            t("July_tr"),
            t("August_tr"),
            t("September_tr"),
            t("October_tr"),
            t("November_tr"),
            t("December_tr")
        ];
        return monthNames[monthNumber - 1] || '';
    };

    // Prefer geetGovindDetails when it matches selected id (detail API), else list match, else first
    const selectedFromList = selectedMagazineId && magazinesDetails?.length
        ? magazinesDetails.find((item: any) => String(item?.id) === String(selectedMagazineId))
        : null;
    const detailMatchesSelected = geetGovindDetails && selectedMagazineId
        && String(geetGovindDetails.id) === String(selectedMagazineId);
    const selectedMagazine = (detailMatchesSelected ? geetGovindDetails : null)
        || selectedFromList
        || geetGovindDetails
        || (magazinesDetails?.length ? magazinesDetails[0] : null)
        || null;

    // Determine which data source to display
    const dataToDisplay = bookDetails || selectedMagazine || kalyanDetails || vivekVaniDetails || geetGovindDetails;

    if (!dataToDisplay) {
        return <Typography variant="body1">No details available.</Typography>;
    }

    const title = bookDetails?.name || selectedMagazine?.name || kalyanDetails?.name || vivekVaniDetails?.name || geetGovindDetails?.name;
    const author = bookDetails?.author || selectedMagazine?.author || kalyanDetails?.author || vivekVaniDetails?.author || geetGovindDetails?.author;
    const language = bookDetails?.bookLanguage
        || selectedMagazine?.magazineLanguage
        || selectedMagazine?.monthlyMagazineLanguage
        || kalyanDetails?.kalyanLanguage
        || kalyanDetails?.kalyanKalpataruLanguage
        || vivekVaniDetails?.vivekVaniLanguage
        || geetGovindDetails?.monthlyMagazineLanguage;
    const thumbnail = bookDetails?.bookThumbPath
        || selectedMagazine?.monthlyMagazineThumbPath
        || kalyanDetails?.kalyanThumbPath
        || kalyanDetails?.kalyanKalpataruThumbPath
        || vivekVaniDetails?.vivekVaniThumbPath
        || geetGovindDetails?.monthlyMagazineThumbPath;
    const month = selectedMagazine?.months || kalyanDetails?.months || vivekVaniDetails?.months || geetGovindDetails?.months;
    const year = selectedMagazine?.years || kalyanDetails?.years || vivekVaniDetails?.years || geetGovindDetails?.years;
    const edition = selectedMagazine?.editionNo || kalyanDetails?.editionNo || vivekVaniDetails?.editionNo || geetGovindDetails?.editionNo;
    const displayMonth = typeof month === 'number' ? getMonthName(month) : month;
    const magazinesList = magazinesDetails && magazinesDetails.length > 0 ? magazinesDetails : [];
    // Prefer explicit lang string (english/hindi) from parent over language id
    const resolvedMagazineLang =
        magazineLang
        || selectedMagazine?.magazineLanguage
        || selectedMagazine?.magazineLangauge
        || selectedMagazine?.monthlyMagazineLanguage
        || magazinesDetails?.[0]?.magazineLanguage
        || magazinesDetails?.[0]?.monthlyMagazineLanguage;

    const StyledCard = styled(Card)(({ theme }) => ({
        backgroundColor: 'white',
        boxShadow: 'none',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: theme.spacing(2),
        borderRadius: 'none'
    }));

    return (
        <>
            <Box sx={{
                backgroundColor: "white",
                display: 'flex',
                margin: '5px',
                height: "204px",
                padding: '15px',
                alignItems: 'flex-start',
            }}>
                <Box sx={{
                    width: '120px',
                    height: '180px',
                    flexShrink: 0,
                    mr: 2,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    overflow: 'hidden',
                }}>
                    {thumbnail && (
                        <img
                            src={thumbnail}
                            alt={title}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                    )}
                </Box>
                <Box sx={{
                    flexGrow: 1,
                    pt: 0,
                    fontSize: 20,
                    fontWeight: 600,
                    fontFamily: 'chanakyaUni',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    height: '100%',
                }}>
                    <Typography variant="h5" sx={{
                        fontSize: 'clamp(22px, 4vw, 20px)',
                        fontWeight: 600,
                        fontFamily: 'chanakyaUni',
                        mb: 1
                    }}>
                        {title && title}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center', mt: 2 }}>
                        <img
                            src={isLiked ? Favfill : favorite}
                            height={35}
                            width={35}
                            alt="Favorite icon"
                            // onClick={onToggleLike}
                            onClick={()=>setShowDownloadPopup(true)}
                            style={{ cursor: 'pointer' }}
                        />
                        <img src={share} height={35} width={35} alt="Share icon" />
                        <img src={whatsapp} height={35} width={35} alt="Whatsapp icon" />
                    </Box>
                    <Button
                    onClick={()=>{
                        //   navigate(`/reader/books/` + bookDetails?.slug, {
                        //                               state: {
                        //                                 bookDetailId: bookDetails?.id,
                        //                                 bookName: bookDetails?.name,
                        //                                 slug: bookDetails?.slug,
                        //                                 label: bookDetails?.label,
                        //                                 type: BookContentType.vivek,
                        //                               },
                        //                             })    

                        setShowDownloadPopup(true)
                    }}
                        variant='contained'
                        style={{ backgroundColor: '#f97316', height: 30, width: '100%' }}
                    >
                        {t("Read_the_book_tr")}
                    </Button>
                </Box>
            </Box>
            {
                author && (
                    <div className='mt-2' style={{ backgroundColor: "white", padding: 17, margin: 5, fontWeight: 600, fontSize: 18 }}>
                        {t("Authors_tr")}
                        <br />
                        {author}
                    </div>
                )
            }
       
            <Box sx={{ flexGrow: 1, padding: 1 }} className="pt-0" >
                <Grid container spacing={1} justifyContent="center" >
                    {
                        year && (
                            <Grid item xs={4} style={{ height: '80px',paddingLeft:5}}>
                                <StyledCard>
                                    <CardContent sx={{ textAlign: 'center' }}>
                                        <Typography component="div" gutterBottom sx={{ color:"#555", fontWeight: 'bold', }}>
                                            {t("Year_tr")}
                                        </Typography>
                                        <Typography sx={{fontSize:17,fontFamily:'chanakyaUni',fontWeight:500}}>
                                            {year}
                                        </Typography>
                                    </CardContent>
                                </StyledCard>
                            </Grid>
                        )
                    }
                    {
                        displayMonth && (
                            <Grid item xs={4} style={{ height: '80px',paddingLeft:5 }}>
                                <StyledCard style={{borderRadius:'none'}}>
                                    <CardContent sx={{ textAlign: 'center' }}>
                                        <Typography component="div" gutterBottom sx={{ color: '#555', fontWeight: 'bold', }}>
                                            {t("Month_tr")}
                                        </Typography>
                                        <Typography sx={{ fontSize:17,fontFamily:'chanakyaUni',fontWeight:500}}>
                                            {displayMonth}
                                        </Typography>
                                    </CardContent>
                                </StyledCard>
                            </Grid>
                        )
                    }
                    {
                        edition && (
                            <Grid item xs={4} style={{ height: '80px',paddingLeft:5}}>
                                <StyledCard style={{borderRadius:'none'}}>
                                    <CardContent sx={{ textAlign: 'center' }}>
                                        <Typography component="div" gutterBottom sx={{ color: '#555', fontWeight: 'bold', }}>
                                            {t("Count_tr")}
                                        </Typography>
                                        <Typography sx={{ fontSize:17,fontFamily:'chanakyaUni',fontWeight:500}}>
                                            {edition}
                                        </Typography>
                                    </CardContent>
                                </StyledCard>
                            </Grid>
                        )
                    }
                </Grid>
                {magazinesList.length > 0 && (
                    <MagazinesCarousel
                        magazinesList={magazinesList}
                        totalRecords={totalRecords}
                        magazineLang={resolvedMagazineLang}
                        magazineLanguageId={magazineLanguageId}
                        itemType={itemType}
                        t={t}
                    />
                )}

  
      {
                language &&
                <div className='mt-2' style={{ backgroundColor: "white", padding: 17, margin:-3, fontWeight: 600, fontSize: 18 }}>
                    {t("Language_tr")}
                    <br />
                    {language}
                </div>
            }
            </Box>
        </>
    );
}

export default BookDetailsCard;